import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Moon, Sun, Command, Menu, X } from "lucide-react";
import { useTheme } from "next-themes";
import { LINKS } from "../lib/content";

const NAV = [
  { label: "About", to: "/about" },
  { label: "Projects", to: "/projects" },
  { label: "Services", to: "/services" },
  { label: "Blog", to: "/blog" },
  { label: "Speaking", to: "/speaking" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = ({ onOpenPalette }) => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 25 });
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="glass bg-background/70 border-b border-border/60">
        <nav className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="font-display text-lg font-semibold tracking-tight">
            Yash Bhatt<span className="text-secondary">.</span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={`text-sm transition-colors duration-200 hover:text-secondary ${location.pathname === item.to ? "text-secondary" : "text-muted-foreground"}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="command-palette-trigger"
              onClick={onOpenPalette}
              aria-label="Open command palette"
              className="hidden sm:flex items-center gap-2 h-9 px-3 rounded-full border border-border text-xs text-muted-foreground hover:border-secondary/50 transition-colors duration-200"
            >
              <Command size={13} /> <span className="font-mono">K</span>
            </button>
            <button
              data-testid="theme-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:border-secondary/50 hover:text-secondary transition-colors duration-200"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <a
              href={LINKS.calendly}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-cta-book"
              className="hidden md:inline-flex h-9 items-center px-5 rounded-full bg-secondary text-secondary-foreground text-sm font-medium hover:opacity-90 active:scale-95 transition-[opacity,background-color] duration-200"
            >
              Book a Consultation
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              className="lg:hidden h-9 w-9 rounded-full border border-border flex items-center justify-center"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </nav>
        <motion.div style={{ scaleX }} className="h-[2px] origin-left bg-gradient-to-r from-violet-500 to-blue-500" />
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass bg-background/95 border-b border-border px-6 py-6 flex flex-col gap-4">
          {NAV.map((item) => (
            <Link key={item.to} to={item.to} className="text-base text-foreground/90">{item.label}</Link>
          ))}
          <a href={LINKS.calendly} target="_blank" rel="noreferrer" className="mt-2 inline-flex h-11 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-sm font-medium">
            Book a Consultation
          </a>
        </div>
      )}
    </header>
  );
};
